import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getCompound, getCategory } from '../data/compounds';
import ScoreGauge from '../components/ScoreGauge';
import ErrorBoundary from '../components/ErrorBoundary';
import InlineEditor from '../components/InlineEditor';
import { useCompoundEditor } from '../hooks/useCompoundEditor';
import './CompoundDetail.css';


// Helper: safely coerce value to array (handles pipe-separated strings from Excel imports)
function toArr(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') return v.split('|').map(s => s.trim()).filter(Boolean);
  return [String(v)];
}

const tabs = ['Overview', 'Pharmacology', 'Lab Synthesis', 'Kg-Scale Mfg', 'Safety / EHS', 'Research', 'Wohler Score', 'Legal & IP', 'Journal / To-Do'];

// Reusable "Coming Soon" placeholder for tabs where the compound data is incomplete
function ComingSoon({ section, compound, category }) {
  return (
    <div className="cd-coming-soon-panel">
      <div className="cd-coming-soon-icon">🚧</div>
      <h3 className="cd-coming-soon-title">{section} — Coming Soon</h3>
      <p className="cd-coming-soon-body">
        This section is flagged in the Wohler research journal for additional work.
        The Wohler R&D team is actively building out graduate-level content
        for every compound in the pipeline. Check back soon for:
      </p>
      <ul className="cd-coming-soon-list">
        {section === 'Lab Synthesis' && (
          <>
            <li>Step-by-step synthesis procedure</li>
            <li>Reagent lists and solvent selection</li>
            <li>Reaction conditions, yields, and purification</li>
            <li>Published synthesis references (DOIs)</li>
          </>
        )}
        {section === 'Kg-Scale Mfg' && (
          <>
            <li>Kilogram-scale procedure using Wohler standard solvents (EtOAc, THF, hexane, heptane, pentane, EtOH, MeOH, IPA, acetone)</li>
            <li>Equipment list, batch plan, and hazards analysis</li>
            <li>Vendor/sourcing notes and cost estimate</li>
            <li>EHS classification and PPE requirements</li>
          </>
        )}
        {section === 'Pharmacology' && (
          <>
            <li>Receptor binding profile and affinity data</li>
            <li>Functional assays (EC50 / Emax / bias factors)</li>
            <li>Pharmacokinetics and metabolism</li>
            <li>Mechanism-of-action summary</li>
          </>
        )}
        {section === 'Safety / EHS' && (
          <>
            <li>Acute and chronic toxicity profile</li>
            <li>Known side effects and contraindications</li>
            <li>Lab PPE and EHS classification</li>
            <li>Drug–drug interaction warnings</li>
          </>
        )}
        {section === 'Research' && (
          <>
            <li>Published studies and key findings</li>
            <li>Clinical trial listings</li>
            <li>Citation library with DOIs / PubMed IDs</li>
          </>
        )}
        {section === 'Wohler Score' && (
          <>
            <li>Benefit / Harm / Net Value gauges</li>
            <li>Evidence confidence rating (HIGH / MODERATE / LOW)</li>
            <li>Go / Hold / Kill decision rationale</li>
          </>
        )}
      </ul>
      <div className="cd-coming-soon-actions">
        <Link to="/compounds" className="cd-cs-btn cd-cs-btn-primary">← All Compounds</Link>
        {category && (
          <Link to={`/categories/${category.id}`} className="cd-cs-btn" style={{ borderColor: category.color, color: category.color }}>
            {category.icon} All {category.name}
          </Link>
        )}
        <Link to="/journal" className="cd-cs-btn">📓 Research Journal</Link>
        <Link to="/" className="cd-cs-btn">🏠 Home</Link>
      </div>
      {compound?.nextSteps?.length > 0 && (
        <div className="cd-coming-soon-todo">
          <strong>Next research tasks on the Wohler to-do list for {compound.name}:</strong>
          <ul>
            {toArr(compound.nextSteps).slice(0, 4).map((t, i) => (
              <li key={i}>☐ {typeof t === 'string' ? t : (t.task || '')}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CompoundDetail() {
  const params = useParams();
  const id = params.compoundId || params.id;
  const navigate = useNavigate();
  const baseCompound = getCompound(id);
  const [activeTab, setActiveTab] = useState('Overview');

  // Chemist/admin inline editing
  const { editableCompound, saveField, canEdit, saving, lastSaved } = useCompoundEditor(baseCompound);
  const compound = editableCompound || baseCompound;

  if (!compound) {
    return (
      <div className="cd-notfound">
        <h2>Compound not found</h2>
        <button onClick={() => navigate('/compounds')}>← Back to Compounds</button>
      </div>
    );
  }

  const category = getCategory(compound.categoryId);

  const statusColors = {
    green: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', text: '#10b981' },
    orange: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#f59e0b' },
    red: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#ef4444' },
  };
  const sc = statusColors[compound.statusColor] || statusColors.green;

  const scheduleStyle = compound.schedulingStatus === 'unscheduled'
    ? { bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.25)', text: '#10b981', label: '✓ Not Federally Scheduled' }
    : { bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)', text: '#ef4444', label: '✗ Scheduled' };

  const confColors = { HIGH: '#10b981', MODERATE: '#f59e0b', LOW: '#94a3b8' };
  const confColor = confColors[compound.evidenceConfidence] || '#94a3b8';

  return (
    <div className="cd-page">
      {/* Breadcrumb */}
      <div className="cd-breadcrumb">
        <div className="cd-breadcrumb-inner">
          <Link to="/">Home</Link>
          <span className="cd-bc-sep">›</span>
          <Link to="/compounds">Compounds</Link>
          {category && (
            <>
              <span className="cd-bc-sep">›</span>
              <Link to={`/categories/${category.id}`} style={{ color: category.color }}>{category.name}</Link>
            </>
          )}
          <span className="cd-bc-sep">›</span>
          <span className="cd-bc-current">{compound.name}</span>
        </div>
      </div>

      {/* Hero Header */}
      <div className="cd-hero" style={{ '--cat-color': category?.color || '#6366f1' }}>
        <div className="cd-hero-bg" style={{ background: `radial-gradient(ellipse 60% 80% at 80% 50%, ${category?.color || '#6366f1'}18 0%, transparent 60%)` }} />
        <div className="cd-hero-inner">
          <div className="cd-hero-left">
            {/* Badges */}
            <div className="cd-hero-badges">
              <span className="cd-badge-status" style={{ background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text }}>
                {compound.status}
              </span>
              <span className="cd-badge-evidence" style={{ color: confColor, borderColor: confColor + '40', background: confColor + '10' }}>
                {compound.evidenceConfidence} Evidence
              </span>
              <span className="cd-badge-schedule" style={{ background: scheduleStyle.bg, border: `1px solid ${scheduleStyle.border}`, color: scheduleStyle.text }}>
                {scheduleStyle.label}
              </span>
              {category && (
                <Link to={`/categories/${category.id}`} className="cd-badge-cat" style={{ background: category.colorLight + '20', border: `1px solid ${category.color}40`, color: category.color }}>
                  {category.icon} {category.name}
                </Link>
              )}
            </div>

            <h1 className="cd-hero-name">{compound.name}</h1>
            <div className="cd-hero-fullname">{compound.fullName}</div>

            {compound.aliases?.length > 0 && (
              <div className="cd-aliases">
                <span className="cd-aliases-label">Also known as:</span>
                {compound.aliases.map(a => <span key={a} className="cd-alias-chip">{a}</span>)}
              </div>
            )}

            <div className="cd-hero-summary">
              <InlineEditor
                value={compound.summary}
                onSave={(v) => saveField('summary', v)}
                label="Summary"
                canEdit={canEdit}
                renderView={(v) => <p style={{ margin: 0 }}>{v}</p>}
              />
            </div>

            {/* Edit mode indicator */}
            {canEdit && (
              <div className="cd-edit-indicator">
                {saving ? (
                  <span className="cd-edit-saving">⏳ Saving…</span>
                ) : lastSaved ? (
                  <span className="cd-edit-saved">✓ Saved {lastSaved.toLocaleTimeString()}</span>
                ) : (
                  <span className="cd-edit-hint">✏️ Click any field to edit</span>
                )}
              </div>
            )}

            <div className="cd-hero-meta">
              <div className="cd-meta-item">
                <span className="cd-meta-label">Formula</span>
                <span className="cd-meta-value cd-formula">{compound.formula}</span>
              </div>
              <div className="cd-meta-item">
                <span className="cd-meta-label">Mol. Weight</span>
                <span className="cd-meta-value">{compound.molecularWeight}</span>
              </div>
            </div>
          </div>

          {/* Score Panel */}
          <div className="cd-hero-scores">
            <div className="cd-scores-title">Research Scores</div>
            <div className="cd-scores-row">
              <div className="cd-score-item">
                <ScoreGauge score={compound.benefitScore} label="Benefit Potential" type="benefit" size="large" />
              </div>
              <div className="cd-score-item">
                <ScoreGauge score={compound.harmScore} label="Risk Score" type="harm" size="large" />
              </div>
            </div>
            <div className="cd-net-score">
              <span className="cd-net-label">Net Research Value</span>
              <span className="cd-net-value" style={{ color: compound.benefitScore - compound.harmScore >= 50 ? '#10b981' : '#f59e0b' }}>
                {compound.benefitScore - compound.harmScore > 0 ? '+' : ''}{compound.benefitScore - compound.harmScore}
              </span>
            </div>
            <div className="cd-score-note">
              Scores reflect current evidence base. Higher benefit + lower risk = stronger research candidacy.
            </div>
          </div>
        </div>
      </div>

      {/* Persistent Nav Rail — always-visible way back */}
      <div className="cd-nav-rail">
        <div className="cd-nav-rail-inner">
          <Link to="/" className="cd-nav-rail-link">🏠 Home</Link>
          <Link to="/compounds" className="cd-nav-rail-link">← All Compounds</Link>
          {category && (
            <Link to={`/categories/${category.id}`} className="cd-nav-rail-link" style={{ color: category.color, borderColor: category.color + '40' }}>
              {category.icon} {category.name}
            </Link>
          )}
          <Link to="/journal" className="cd-nav-rail-link">📓 Journal</Link>
          <div className="cd-nav-rail-spacer" />
          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
            {compound.name}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="cd-tabs-bar">
        <div className="cd-tabs-inner">
          {tabs.map(tab => (
            <button
              key={tab}
              className={`cd-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
              style={activeTab === tab ? { color: category?.color, borderColor: category?.color } : {}}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="cd-content">
        <div className="cd-content-inner">
          <ErrorBoundary>

          {activeTab === 'Overview' && (
            <div className="cd-tab-content">
              {compound.enhancedContent && (
                <div className="cd-panel cd-enhanced-panel">
                  <div className="cd-panel-header">
                    <span className="cd-panel-icon">📖</span>
                    Enhanced Research Page
                    {compound.enhancedContentSource && (
                      <span className="cd-enhanced-source">{compound.enhancedContentSource}</span>
                    )}
                  </div>
                  <div className="cd-enhanced-markdown">
                    <ReactMarkdown>{compound.enhancedContent}</ReactMarkdown>
                  </div>
                </div>
              )}
              <div className="cd-two-col">
                {/* Pros */}
                <div className="cd-panel cd-pros">
                  <div className="cd-panel-header" style={{ color: '#10b981' }}>
                    <span className="cd-panel-icon">✓</span>
                    Research Advantages
                  </div>
                  <ul className="cd-list cd-list-pro">
                    {compound.pros?.map((pro, i) => (
                      <li key={i}>{pro}</li>
                    ))}
                  </ul>
                </div>

                {/* Cons */}
                <div className="cd-panel cd-cons">
                  <div className="cd-panel-header" style={{ color: '#f59e0b' }}>
                    <span className="cd-panel-icon">⚠</span>
                    Research Considerations
                  </div>
                  <ul className="cd-list cd-list-con">
                    {compound.cons?.map((con, i) => (
                      <li key={i}>{con}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Scheduling Detail */}
              <div className="cd-panel cd-scheduling-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">⚖️</span>
                  Legal / Scheduling Status
                </div>
                <div className="cd-scheduling-content">
                  <div className="cd-scheduling-status" style={{ background: scheduleStyle.bg, border: `1px solid ${scheduleStyle.border}`, color: scheduleStyle.text }}>
                    {scheduleStyle.label}
                  </div>
                  <p className="cd-scheduling-note">{compound.schedulingNote}</p>
                  <div className="cd-scheduling-warning">
                    <strong>⚠️ Important:</strong> Scheduling status reflects federal DEA status at time of research compilation. Always verify current status via the DEA Diversion Control Division and Federal Register before synthesis. State laws may independently schedule compounds.
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Pharmacology' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">🧬</span>
                  Pharmacological Profile
                </div>
                <div className="cd-pharm-grid">
                  <div className="cd-pharm-item">
                    <div className="cd-pharm-label">Molecular Formula</div>
                    <div className="cd-pharm-value cd-formula">{compound.formula || '—'}</div>
                  </div>
                  <div className="cd-pharm-item">
                    <div className="cd-pharm-label">Molecular Weight</div>
                    <div className="cd-pharm-value">{compound.molecularWeight || '—'}</div>
                  </div>
                  <div className="cd-pharm-item">
                    <div className="cd-pharm-label">Evidence Confidence</div>
                    <div className="cd-pharm-value" style={{ color: confColor }}>{compound.evidenceConfidence || '—'}</div>
                  </div>
                  <div className="cd-pharm-item">
                    <div className="cd-pharm-label">Research Status</div>
                    <div className="cd-pharm-value" style={{ color: sc.text }}>{compound.status || '—'}</div>
                  </div>
                </div>
              </div>

              {compound.pharmacology?.targets && toArr(compound.pharmacology.targets).length > 0 && (
                <div className="cd-panel">
                  <div className="cd-panel-header">
                    <span className="cd-panel-icon">🎯</span>
                    Receptor Binding & Functional Targets
                  </div>
                  <div className="cd-target-table">
                    <div className="cd-target-row cd-target-head">
                      <div>Receptor / Target</div>
                      <div>Activity</div>
                      <div>Value</div>
                    </div>
                    {toArr(compound.pharmacology.targets).map((t, i) => (
                      <div className="cd-target-row" key={i}>
                        <div className="cd-target-receptor">{t.receptor}</div>
                        <div>{t.activity}</div>
                        <div className="cd-target-value">{t.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(compound.pharmacology?.mechanism || compound.pharmacology?.halfLife || compound.pharmacology?.duration || compound.pharmacology?.metabolism || compound.pharmacology?.htrED50) && (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">🧪</span>Mechanism & PK</div>
                  {compound.pharmacology.mechanism && (
                    <div className="cd-pk-block">
                      <strong>Mechanism:</strong>
                      <InlineEditor
                        value={compound.pharmacology.mechanism}
                        onSave={(v) => saveField('pharmacology.mechanism', v)}
                        label="Mechanism"
                        canEdit={canEdit}
                        renderView={(v) => <span>{v}</span>}
                      />
                    </div>
                  )}
                  {compound.pharmacology.htrED50 && <div className="cd-pk-block"><strong>HTR ED50:</strong> {compound.pharmacology.htrED50}</div>}
                  {compound.pharmacology.duration && (
                    <div className="cd-pk-block">
                      <strong>Duration:</strong>
                      <InlineEditor
                        value={compound.pharmacology.duration}
                        onSave={(v) => saveField('pharmacology.duration', v)}
                        label="Duration"
                        canEdit={canEdit}
                        multiline={false}
                        renderView={(v) => <span>{v}</span>}
                      />
                    </div>
                  )}
                  {compound.pharmacology.halfLife && (
                    <div className="cd-pk-block">
                      <strong>Half-life:</strong>
                      <InlineEditor
                        value={compound.pharmacology.halfLife}
                        onSave={(v) => saveField('pharmacology.halfLife', v)}
                        label="Half-life"
                        canEdit={canEdit}
                        multiline={false}
                        renderView={(v) => <span>{v}</span>}
                      />
                    </div>
                  )}
                  {compound.pharmacology.metabolism && (
                    <div className="cd-pk-block">
                      <strong>Metabolism:</strong>
                      <InlineEditor
                        value={compound.pharmacology.metabolism}
                        onSave={(v) => saveField('pharmacology.metabolism', v)}
                        label="Metabolism"
                        canEdit={canEdit}
                        renderView={(v) => <span>{v}</span>}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">📊</span>
                  Score Breakdown
                </div>
                <div className="cd-score-bars">
                  <div className="cd-score-bar-row">
                    <span className="cd-score-bar-label">Benefit Potential</span>
                    <div className="cd-score-bar-track">
                      <div className="cd-score-bar-fill" style={{ width: `${compound.benefitScore}%`, background: compound.benefitScore >= 80 ? '#10b981' : compound.benefitScore >= 60 ? '#f59e0b' : '#ef4444' }} />
                    </div>
                    <span className="cd-score-bar-val">{compound.benefitScore}/100</span>
                  </div>
                  <div className="cd-score-bar-row">
                    <span className="cd-score-bar-label">Risk Score</span>
                    <div className="cd-score-bar-track">
                      <div className="cd-score-bar-fill" style={{ width: `${compound.harmScore}%`, background: compound.harmScore <= 25 ? '#10b981' : compound.harmScore <= 45 ? '#f59e0b' : '#ef4444' }} />
                    </div>
                    <span className="cd-score-bar-val">{compound.harmScore}/100</span>
                  </div>
                  <div className="cd-score-bar-row">
                    <span className="cd-score-bar-label">Net Research Value</span>
                    <div className="cd-score-bar-track">
                      <div className="cd-score-bar-fill" style={{ width: `${Math.max(0, compound.benefitScore - compound.harmScore)}%`, background: '#6366f1' }} />
                    </div>
                    <span className="cd-score-bar-val">{compound.benefitScore - compound.harmScore}/100</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Lab Synthesis' && (
            <div className="cd-tab-content">
              {compound.labSynthesis ? (
                <>
                  <div className="cd-panel">
                    <div className="cd-panel-header">
                      <span className="cd-panel-icon">⚗️</span>
                      {compound.labSynthesis.title || 'Laboratory-Scale Synthesis'}
                    </div>
                    {compound.labSynthesis.overview && (
                      <div className="cd-synthesis-text" style={{marginBottom: 20}}>
                        <strong>Overview:</strong>
                        <InlineEditor
                          value={compound.labSynthesis.overview}
                          onSave={(v) => saveField('labSynthesis.overview', v)}
                          label="Lab Synthesis Overview"
                          canEdit={canEdit}
                          renderView={(v) => <span> {v}</span>}
                        />
                      </div>
                    )}
                    {compound.labSynthesis.steps?.map((step, i) => (
                      <div key={i} className="cd-synth-step">
                        <div className="cd-synth-step-header">
                          <span className="cd-synth-step-num">{step.step || (i + 1)}</span>
                          <span className="cd-synth-step-title">{step.title}</span>
                        </div>
                        <div className="cd-synth-step-desc">{step.description}</div>
                        {(step.solvent || step.keyReagents || step.yield) && (
                          <div className="cd-synth-step-meta">
                            {step.solvent && <span><strong>Solvent:</strong> {step.solvent}</span>}
                            {step.keyReagents && <span><strong>Reagents:</strong> {step.keyReagents}</span>}
                            {step.yield && <span><strong>Yield:</strong> {step.yield}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                    {compound.labSynthesis.totalYield && (
                      <div className="cd-synth-totals"><strong>Total overall yield:</strong> {compound.labSynthesis.totalYield}</div>
                    )}
                    {compound.labSynthesis.purity && (
                      <div className="cd-synth-totals"><strong>Purity target:</strong> {compound.labSynthesis.purity}</div>
                    )}
                  </div>
                  {compound.labSynthesis.notes && (
                    <div className="cd-panel">
                      <div className="cd-panel-header"><span className="cd-panel-icon">📝</span>Synthesis Notes</div>
                      <InlineEditor
                        value={compound.labSynthesis.notes}
                        onSave={(v) => saveField('labSynthesis.notes', v)}
                        label="Synthesis Notes"
                        canEdit={canEdit}
                        renderView={(v) => <div className="cd-synthesis-text">{v}</div>}
                      />
                    </div>
                  )}
                </>
              ) : compound.synthesisRoute ? (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">⚗️</span>Synthesis Route</div>
                  <div className="cd-synthesis-text">{compound.synthesisRoute}</div>
                </div>
              ) : (
                <ComingSoon section="Lab Synthesis" compound={compound} category={category} />
              )}
              {compound.publishedResearch?.synthesisRefs && (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">📚</span>Synthesis References</div>
                  <ul className="cd-ref-list">
                    {toArr(compound.publishedResearch.synthesisRefs).map((ref, i) => (
                      <li key={i} className="cd-ref-item">
                        <span className="cd-ref-num">{i + 1}</span>
                        <span>{typeof ref === 'string' ? ref : `${ref.authors || ''} ${ref.title ? '— ' + ref.title : ''}${ref.journal ? ' (' + ref.journal + ')' : ''}`}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {compound.synthesisRefs && !compound.publishedResearch?.synthesisRefs && (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">📚</span>Synthesis References</div>
                  <ul className="cd-ref-list">
                    {toArr(compound.synthesisRefs).map((ref, i) => (
                      <li key={i} className="cd-ref-item"><span className="cd-ref-num">{i + 1}</span><span>{ref}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Kg-Scale Mfg' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">🏭</span>
                  Kilogram-Scale Manufacturing Plan
                </div>
                <div className="cd-solvent-bar">
                  <strong>Wohler Standard Solvents:</strong> Ethyl Acetate • THF • Hexane • Heptane • Pentane • Ethanol • Methanol • IPA • Acetone
                </div>
                {compound.kgScaleSynthesis ? (
                  <>
                    {compound.kgScaleSynthesis.title && (
                      <h3 className="cd-kg-title">{compound.kgScaleSynthesis.title}</h3>
                    )}
                    {compound.kgScaleSynthesis.feasibility && (
                      <div className="cd-kg-feasibility">
                        <strong>Feasibility:</strong>
                        <InlineEditor
                          value={compound.kgScaleSynthesis.feasibility}
                          onSave={(v) => saveField('kgScaleSynthesis.feasibility', v)}
                          label="KG-Scale Feasibility"
                          canEdit={canEdit}
                          renderView={(v) => <span> {v}</span>}
                        />
                      </div>
                    )}
                    {compound.kgScaleSynthesis.overview && (
                      <div className="cd-kg-block">
                        <h4>Overview</h4>
                        <InlineEditor
                          value={compound.kgScaleSynthesis.overview}
                          onSave={(v) => saveField('kgScaleSynthesis.overview', v)}
                          label="KG-Scale Overview"
                          canEdit={canEdit}
                          renderView={(v) => <p style={{ margin: 0 }}>{v}</p>}
                        />
                      </div>
                    )}
                    {(compound.kgScaleSynthesis.targetScale || compound.kgScaleSynthesis.targetBatch) && (
                      <div className="cd-kg-block">
                        <h4>Target Scale</h4>
                        <p>{compound.kgScaleSynthesis.targetScale || compound.kgScaleSynthesis.targetBatch}</p>
                      </div>
                    )}
                    {compound.kgScaleSynthesis.batchPlan && typeof compound.kgScaleSynthesis.batchPlan === 'object' && (
                      <div className="cd-kg-block">
                        <h4>Batch Plan</h4>
                        {compound.kgScaleSynthesis.batchPlan.targetBatch && (
                          <p><strong>Target:</strong> {compound.kgScaleSynthesis.batchPlan.targetBatch}</p>
                        )}
                        {compound.kgScaleSynthesis.batchPlan.startingMaterial && (
                          <p><strong>Starting Material:</strong> {compound.kgScaleSynthesis.batchPlan.startingMaterial}</p>
                        )}
                        {compound.kgScaleSynthesis.batchPlan.keyVolumes && Array.isArray(compound.kgScaleSynthesis.batchPlan.keyVolumes) && (
                          <>
                            <p><strong>Key Volumes / Reagents:</strong></p>
                            <ul className="cd-list">
                              {compound.kgScaleSynthesis.batchPlan.keyVolumes.map((v, i) => <li key={i}>{v}</li>)}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                    {compound.kgScaleSynthesis.batchPlan && typeof compound.kgScaleSynthesis.batchPlan === 'string' && (
                      <div className="cd-kg-block">
                        <h4>Batch Plan</h4>
                        <p>{compound.kgScaleSynthesis.batchPlan}</p>
                      </div>
                    )}
                    {(compound.kgScaleSynthesis.solventsUsed || compound.kgScaleSynthesis.standardSolventsUsed) && (
                      <div className="cd-kg-block">
                        <h4>Solvents Used (Wohler-Standard)</h4>
                        <p>{(() => {
                          const sv = compound.kgScaleSynthesis.solventsUsed || compound.kgScaleSynthesis.standardSolventsUsed;
                          return Array.isArray(sv) ? sv.join(' • ') : String(sv);
                        })()}</p>
                      </div>
                    )}
                    {compound.kgScaleSynthesis.standardSolventsNotUsed && Array.isArray(compound.kgScaleSynthesis.standardSolventsNotUsed) && compound.kgScaleSynthesis.standardSolventsNotUsed.length > 0 && (
                      <div className="cd-kg-block">
                        <h4>Non-Standard Solvents Avoided</h4>
                        <ul className="cd-list">
                          {compound.kgScaleSynthesis.standardSolventsNotUsed.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                    {(compound.kgScaleSynthesis.steps || compound.kgScaleSynthesis.procedure) && (
                      <div className="cd-kg-block">
                        <h4>Scaled Procedure</h4>
                        {(() => {
                          const proc = compound.kgScaleSynthesis.steps || compound.kgScaleSynthesis.procedure;
                          if (Array.isArray(proc)) {
                            return (
                              <ol className="cd-kg-proc">
                                {proc.map((s, i) => (
                                  <li key={i}>
                                    {typeof s === 'string'
                                      ? s
                                      : (s.description || s.title || JSON.stringify(s))}
                                  </li>
                                ))}
                              </ol>
                            );
                          }
                          return <p>{String(proc)}</p>;
                        })()}
                      </div>
                    )}
                    {compound.kgScaleSynthesis.equipment && (
                      <div className="cd-kg-block">
                        <h4>Equipment Required</h4>
                        <ul className="cd-list">
                          {(Array.isArray(compound.kgScaleSynthesis.equipment) ? compound.kgScaleSynthesis.equipment : [compound.kgScaleSynthesis.equipment]).map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </div>
                    )}
                    {compound.kgScaleSynthesis.hazards && (
                      <div className="cd-kg-block cd-kg-hazard">
                        <h4>⚠️ Scale-up Hazards / EHS Controls</h4>
                        {Array.isArray(compound.kgScaleSynthesis.hazards) ? (
                          <ul className="cd-list">{compound.kgScaleSynthesis.hazards.map((h, i) => <li key={i}>{h}</li>)}</ul>
                        ) : (
                          <p>{compound.kgScaleSynthesis.hazards}</p>
                        )}
                      </div>
                    )}
                    {compound.kgScaleSynthesis.vendorNotes && (
                      <div className="cd-kg-block">
                        <h4>Vendor / Sourcing Notes</h4>
                        {Array.isArray(compound.kgScaleSynthesis.vendorNotes) ? (
                          <ul className="cd-list">{compound.kgScaleSynthesis.vendorNotes.map((v, i) => <li key={i}>{v}</li>)}</ul>
                        ) : (
                          <p>{compound.kgScaleSynthesis.vendorNotes}</p>
                        )}
                      </div>
                    )}
                    {(compound.kgScaleSynthesis.estCostPerKg || compound.kgScaleSynthesis.costEstimate) && (
                      <div className="cd-kg-block cd-kg-cost">
                        <h4>💵 Cost Estimate (Kg Scale)</h4>
                        <p>{compound.kgScaleSynthesis.estCostPerKg || compound.kgScaleSynthesis.costEstimate}</p>
                      </div>
                    )}
                    {(compound.kgScaleSynthesis.yield || compound.kgScaleSynthesis.yieldTarget) && (
                      <div className="cd-kg-block">
                        <h4>Scale-up Yield Target</h4>
                        <p>{compound.kgScaleSynthesis.yield || compound.kgScaleSynthesis.yieldTarget}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <ComingSoon section="Kg-Scale Mfg" compound={compound} category={category} />
                )}
              </div>
            </div>
          )}

          {activeTab === 'Safety / EHS' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">🔴</span>
                  Known Side Effects & Safety Profile
                </div>
                <ul className="cd-list cd-list-safety">
                  {(compound.safety?.sideEffects || compound.sideEffects || []).map((effect, i) => (
                    <li key={i}>{typeof effect === 'string' ? effect : JSON.stringify(effect)}</li>
                  ))}
                </ul>
                {compound.safety?.acuteToxicity && (
                  <div className="cd-safety-block">
                    <h4>Acute Toxicity</h4>
                    <InlineEditor
                      value={compound.safety.acuteToxicity}
                      onSave={(v) => saveField('safety.acuteToxicity', v)}
                      label="Acute Toxicity"
                      canEdit={canEdit}
                      renderView={(v) => <p style={{ margin: 0 }}>{v}</p>}
                    />
                  </div>
                )}
                {compound.safety?.chronicRisks && (
                  <div className="cd-safety-block">
                    <h4>Chronic / Long-Term Risks</h4>
                    <InlineEditor
                      value={compound.safety.chronicRisks}
                      onSave={(v) => saveField('safety.chronicRisks', v)}
                      label="Chronic Risks"
                      canEdit={canEdit}
                      renderView={(v) => <p style={{ margin: 0 }}>{v}</p>}
                    />
                  </div>
                )}
                {compound.safety?.ehsClassification && (
                  <div className="cd-safety-block">
                    <h4>EHS Classification (lab/plant)</h4>
                    <InlineEditor
                      value={compound.safety.ehsClassification}
                      onSave={(v) => saveField('safety.ehsClassification', v)}
                      label="EHS Classification"
                      canEdit={canEdit}
                      multiline={false}
                      renderView={(v) => <p style={{ margin: 0 }}>{v}</p>}
                    />
                  </div>
                )}
                {compound.safety?.ppe && (
                  <div className="cd-safety-block"><h4>Required PPE</h4><p>{Array.isArray(compound.safety.ppe) ? compound.safety.ppe.join(' • ') : compound.safety.ppe}</p></div>
                )}
                {compound.safety?.contraindications && (
                  <div className="cd-safety-block"><h4>Contraindications / Drug–Drug Interactions</h4><p>{Array.isArray(compound.safety.contraindications) ? compound.safety.contraindications.join(' • ') : compound.safety.contraindications}</p></div>
                )}
              </div>
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">📊</span>
                  Risk Assessment
                </div>
                <div className="cd-risk-gauge-row">
                  <ScoreGauge score={compound.harmScore} label="Overall Risk Score" type="harm" size="large" />
                  <div className="cd-risk-interpretation">
                    <div className={`cd-risk-level ${compound.harmScore <= 25 ? 'low' : compound.harmScore <= 45 ? 'moderate' : 'high'}`}>
                      {compound.harmScore <= 25 ? '🟢 LOW RISK' : compound.harmScore <= 45 ? '🟡 MODERATE RISK' : '🔴 HIGH RISK'}
                    </div>
                    <p>
                      {compound.harmScore <= 25
                        ? 'Current evidence suggests a favorable safety profile for controlled research use. Standard laboratory safety protocols apply.'
                        : compound.harmScore <= 45
                        ? 'Moderate risk profile. Research protocols should include enhanced safety monitoring, dose escalation design, and appropriate exclusion criteria.'
                        : 'Elevated risk profile. Research use requires rigorous safety protocols, close medical supervision, and comprehensive exclusion criteria.'}
                    </p>
                  </div>
                </div>
                <div className="cd-safety-disclaimer">
                  <strong>⚠️ Research Use Only.</strong> This safety profile is based on available preclinical and observational data. All compounds are for laboratory research only. Not for human consumption outside of properly approved IRB/IND protocols.
                </div>
              </div>
            </div>
          )}

          {activeTab === 'Research' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">🔬</span>
                  Known Research & Published Studies
                </div>
                <ul className="cd-list cd-list-research">
                  {(compound.publishedResearch?.keyFindings || compound.knownResearch || []).map((item, i) => (
                    <li key={i}>{typeof item === 'string' ? item : (item.title || JSON.stringify(item))}</li>
                  ))}
                </ul>
              </div>
              {(compound.publishedResearch?.citations || compound.sources) && (
                <div className="cd-panel">
                  <div className="cd-panel-header">
                    <span className="cd-panel-icon">🔗</span>
                    Source References
                  </div>
                  <div className="cd-sources">
                    {(compound.publishedResearch?.citations || compound.sources || []).map((src, i) => {
                      const url = src.url || src.doi || '#';
                      const title = src.title || src.citation || `Reference ${i + 1}`;
                      const journal = src.journal || src.authors || src.year || '';
                      return (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="cd-source-card">
                          <div className="cd-source-title">{title}</div>
                          <div className="cd-source-journal">{journal}</div>
                          {url !== '#' && <div className="cd-source-url">{url}</div>}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              {compound.publishedResearch?.clinicalTrials && (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">🧪</span>Clinical Trials</div>
                  <ul className="cd-list">
                    {toArr(compound.publishedResearch.clinicalTrials).map((t, i) => (
                      <li key={i}>{typeof t === 'string' ? t : `${t.id || ''} — ${t.title || ''} (${t.phase || ''})`}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'Wohler Score' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header"><span className="cd-panel-icon">📊</span>Wohler Scoring System</div>
                <div className="cd-risk-gauge-row">
                  <ScoreGauge score={compound.benefitScore} label="Benefit" type="benefit" size="large" />
                  <ScoreGauge score={compound.harmScore} label="Harm / Risk" type="harm" size="large" />
                  <ScoreGauge score={Math.max(0, compound.benefitScore - compound.harmScore)} label="Net Research Value" type="benefit" size="large" />
                </div>
                <div className="cd-score-details">
                  <div><strong>Evidence Confidence:</strong> <span style={{color: confColor}}>{compound.evidenceConfidence || (compound.wohlerScoring && compound.wohlerScoring.evidenceConfidence) || 'MODERATE'}</span></div>
                  <div><strong>Go/Hold/Kill Decision:</strong> {compound.goDecision || compound.status || 'Under Review'}</div>
                  {compound.priorityTier && <div><strong>Priority Tier:</strong> {compound.priorityTier}</div>}
                  {compound.program && <div><strong>Research Program:</strong> {compound.program}</div>}
                </div>
                {compound.wohlerScoring && (
                  <div className="cd-scoring-rationale">
                    {compound.wohlerScoring.benefitRationale && (
                      <div className="cd-score-block cd-score-benefit">
                        <h4>✓ Benefit Rationale</h4>
                        <p>{compound.wohlerScoring.benefitRationale}</p>
                      </div>
                    )}
                    {compound.wohlerScoring.harmRationale && (
                      <div className="cd-score-block cd-score-harm">
                        <h4>⚠ Harm / Risk Rationale</h4>
                        <p>{compound.wohlerScoring.harmRationale}</p>
                      </div>
                    )}
                    {compound.wohlerScoring.evidenceBasis && (
                      <div className="cd-score-block">
                        <h4>📚 Evidence Basis</h4>
                        <p>{compound.wohlerScoring.evidenceBasis}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'Journal / To-Do' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header"><span className="cd-panel-icon">📓</span>Research Journal — Next Steps & Open Questions</div>
                {compound.nextSteps && compound.nextSteps.length > 0 ? (
                  <ul className="cd-list cd-todo-list">
                    {toArr(compound.nextSteps).map((item, i) => (
                      <li key={i}>
                        <input type="checkbox" disabled style={{marginRight:10}} />
                        {typeof item === 'string' ? item : (item.task || JSON.stringify(item))}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="cd-muted">No open research tasks recorded for this compound yet. Add entries to <code>nextSteps</code> in the compound data file to populate the journal.</p>
                )}
                <div className="cd-journal-meta">
                  <div><strong>Status:</strong> {compound.status || '—'}</div>
                  <div><strong>Last reviewed:</strong> {compound.lastReviewed || 'Pending'}</div>
                  <div><strong>Owner:</strong> {compound.owner || 'Wohler R&D'}</div>
                </div>
              </div>
              {compound.internalNotes && (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">🗒️</span>Internal Notes</div>
                  <InlineEditor
                    value={compound.internalNotes}
                    onSave={(v) => saveField('internalNotes', v)}
                    label="Internal Notes"
                    canEdit={canEdit}
                    renderView={(v) => <div className="cd-synthesis-text">{v}</div>}
                  />
                </div>
              )}
              {/* Chemist/admin can add notes even if none exist yet */}
              {!compound.internalNotes && canEdit && (
                <div className="cd-panel">
                  <div className="cd-panel-header"><span className="cd-panel-icon">🗒️</span>Internal Notes</div>
                  <InlineEditor
                    value=""
                    onSave={(v) => saveField('internalNotes', v)}
                    label="Internal Notes"
                    canEdit={canEdit}
                    renderView={() => <p className="cd-muted">No internal notes yet. Click ✏️ to add.</p>}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'Legal & IP' && (
            <div className="cd-tab-content">
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">⚖️</span>
                  Scheduling & Legal Status
                </div>
                <div className="cd-scheduling-content">
                  <div className="cd-scheduling-status" style={{ background: scheduleStyle.bg, border: `1px solid ${scheduleStyle.border}`, color: scheduleStyle.text }}>
                    {scheduleStyle.label}
                  </div>
                  <p className="cd-scheduling-note">{compound.schedulingNote}</p>
                </div>
              </div>
              <div className="cd-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">💡</span>
                  Patent Information
                </div>
                <div className="cd-patent-text">{compound.patentInfo}</div>
              </div>
              <div className="cd-panel cd-legal-warning-panel">
                <div className="cd-panel-header">
                  <span className="cd-panel-icon">⚠️</span>
                  Legal Compliance Checklist
                </div>
                <div className="cd-legal-checklist">
                  {[
                    'Verify current DEA federal scheduling status via DEA Diversion Control Division',
                    'Check state-level controlled substance analog laws for operating state(s)',
                    'Consult regulatory attorney re: Federal Analogue Act applicability',
                    'Establish IRB-registered university research group relationship before synthesis',
                    'Document research intent thoroughly — institutional partnerships essential',
                    'Monitor DEA Federal Register weekly for emergency scheduling actions',
                    'Ensure "for research use only" labeling on all product containers',
                    'Maintain chain-of-custody documentation for all research supply'
                  ].map((item, i) => (
                    <div key={i} className="cd-check-item">
                      <span className="cd-check-box">☐</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          </ErrorBoundary>
        </div>
      </div>

      {/* Back */}
      <div className="cd-back-bar">
        <div className="cd-content-inner">
          <Link to="/compounds" className="cd-back-link">← Back to All Compounds</Link>
          {category && (
            <Link to={`/categories/${category.id}`} className="cd-back-link" style={{ color: category.color }}>
              View all {category.name} compounds →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}